import {Component, inject, OnDestroy, OnInit, signal, WritableSignal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ICompanyDetailsSimplified, ICompanyListPaged, IExpertClientDetail} from '@models/company.interface';
import {CompanyService} from '@services/company.service';
import {NotificationService} from '@services/frontend/notification.service';
import {PappersService} from '@services/pappers.service';
import {ICompanySearchSuggestion} from '@models/pappers.interface';
import {catchError, forkJoin, of, switchMap} from 'rxjs';
import {ICompanyCertification} from '@models/workspace.interface';
import {AuthService} from '@services/auth.service';
import {MAX_ACCOUNTANT_COMPANIES} from '@constants/accountant-limits';
import {ModalService} from '@services/frontend/modal.service';
import {
  ExpertBulkImportDialog
} from '@components/expert/expert-clients/expert-bulk-import-dialog/expert-bulk-import-dialog';
import {IPasswordUpdateRequest} from '@models/compteAdministration.interface';
import {ICompanyContactUpdate} from '@models/workspace.interface';

type ExpertStatusKind = 'ok' | 'error' | 'pending';

@Component({
  selector: 'app-expert-clients',
  imports: [FormsModule],
  templateUrl: './expert-clients.html',
})
export class ExpertClients implements OnInit, OnDestroy {
  readonly maxCompanies = MAX_ACCOUNTANT_COMPANIES;
  readonly pageSize = 20;
  /** Affiché à la place du mot de passe tant que l'utilisateur n'a pas cliqué sur « Révéler ». */
  readonly storedPasswordPlaceholder = '••••••••••••••••';

  private readonly companyService = inject(CompanyService);
  private readonly notificationService = inject(NotificationService);
  private readonly pappersService = inject(PappersService);
  private readonly authService = inject(AuthService);
  private readonly modalService = inject(ModalService);

  companies: WritableSignal<ICompanyDetailsSimplified[]> = signal([]);
  totalClients = signal(0);
  listPage = signal(0);
  listTotalPages = signal(0);
  listSearchInput = signal('');
  listSearchDebounce?: number;

  addQuery = signal('');
  addSearching = signal(false);
  addSuggestions: WritableSignal<ICompanySearchSuggestion[]> = signal([]);
  showAddSuggestions = signal(false);
  selectedAddSuggestion: WritableSignal<ICompanySearchSuggestion | null> = signal(null);
  addImpotsNom = signal('');
  addImpotsPass = signal('');
  addingClient = signal(false);
  private addSearchTimeout?: number;

  expandedRows = signal<Set<string>>(new Set());
  rowDetails = signal<Record<string, IExpertClientDetail | undefined>>({});
  rowDetailLoading = signal<Record<string, boolean>>({});
  rowPasswordDraft = signal<Record<string, { nom: string; pass: string }>>({});
  rowContactDraft = signal<Record<string, { name: string; mail: string }>>({});
  rowSaving = signal<Record<string, boolean>>({});
  rowContactSaving = signal<Record<string, boolean>>({});
  rowRevealing = signal<Record<string, boolean>>({});
  rowPasswordPlainVisible = signal<Record<string, boolean>>({});
  rowPasswordUserEdited = signal<Record<string, boolean>>({});
  rowCfeDownloading = signal<Record<string, boolean>>({});

  downloading = signal(false);
  downloadingCfeZip = signal(false);
  activeTab = signal<'clients' | 'portal'>('clients');
  loading = signal(false);
  showInvitePanel = signal(false);
  inviteEmail = signal('');
  inviteName = signal('');
  inviteSiren = signal('');

  ngOnInit(): void {
    this.refreshClientsPaged();
  }

  ngOnDestroy(): void {
    clearTimeout(this.addSearchTimeout);
    clearTimeout(this.listSearchDebounce);
  }

  refreshClientsPaged(): void {
    this.loading.set(true);
    const q = this.listSearchInput().trim();
    this.companyService.getCompaniesPaged(this.listPage(), this.pageSize, q || undefined).subscribe({
      next: (page: ICompanyListPaged) => {
        this.companies.set(page.content);
        this.totalClients.set(page.totalElements);
        this.listTotalPages.set(page.totalPages);
        this.loading.set(false);
        this.prefetchListStatuses(page.content);
      },
      error: () => {
        this.companies.set([]);
        this.totalClients.set(0);
        this.listTotalPages.set(0);
        this.loading.set(false);
      },
    });
  }

  onListSearchInput(value: string): void {
    this.listSearchInput.set(value);
    clearTimeout(this.listSearchDebounce);
    this.listSearchDebounce = window.setTimeout(() => {
      this.listPage.set(0);
      this.refreshClientsPaged();
    }, 400);
  }

  goPrevPage(): void {
    if (this.listPage() <= 0) {
      return;
    }
    this.listPage.update((p) => p - 1);
    this.refreshClientsPaged();
  }

  goNextPage(): void {
    if (this.listPage() >= this.listTotalPages() - 1) {
      return;
    }
    this.listPage.update((p) => p + 1);
    this.refreshClientsPaged();
  }

  onAddQueryInput(value: string): void {
    this.addQuery.set(value);
    this.selectedAddSuggestion.set(null);
    clearTimeout(this.addSearchTimeout);
    if (value.trim().length < 2) {
      this.addSuggestions.set([]);
      this.showAddSuggestions.set(false);
      return;
    }
    this.addSearchTimeout = window.setTimeout(() => {
      this.addSearching.set(true);
      this.pappersService.searchCompanies(value.trim(), true).subscribe({
        next: (results) => {
          this.addSearching.set(false);
          this.addSuggestions.set(results);
          this.showAddSuggestions.set(true);
        },
        error: () => {
          this.addSearching.set(false);
          this.addSuggestions.set([]);
          this.showAddSuggestions.set(true);
        },
      });
    }, 300);
  }

  selectAddSuggestion(suggestion: ICompanySearchSuggestion): void {
    if (!suggestion.siret) {
      this.notificationService.error('SIRET indisponible pour cette entreprise', 3000);
      return;
    }
    this.addQuery.set(suggestion.companyName || suggestion.siret);
    this.selectedAddSuggestion.set(suggestion);
    this.addImpotsNom.set('');
    this.addImpotsPass.set('');
    this.showAddSuggestions.set(false);
  }

  atCompanyLimit(): boolean {
    return this.totalClients() >= this.maxCompanies;
  }

  addClient(): void {
    const result = this.selectedAddSuggestion();
    const siret = result?.siret;
    if (!siret) {
      return;
    }
    if (this.atCompanyLimit()) {
      this.notificationService.error(
        `Limite de ${this.maxCompanies} entreprises atteinte. Supprimez un client pour en ajouter un autre.`,
        4000
      );
      return;
    }
    const impotsNom = this.addImpotsNom().trim();
    const impotsPass = this.addImpotsPass().trim();
    const saveImpots = impotsNom.length > 0 && impotsPass.length > 0;

    this.addingClient.set(true);
    this.companyService.createCompanyWithoutExternalAccount(siret).pipe(
      switchMap(() => {
        if (!saveImpots) {
          return of(null);
        }
        const body: IPasswordUpdateRequest = {
          nomUtilisateurUrssaf: null,
          passwordUrssaf: null,
          typeCompteUrssaf: null,
          nomUtilisateurImpotsGouv: impotsNom,
          passwordImpotsGouv: impotsPass,
          nomUtilisateurProvigis: null,
          passwordProvigis: null,
          nomUtilisateurIdenum: null,
          passwordIdenum: null,
          passwordProbtp: null,
          nomUtilisateurCibtp: null,
          passwordCibtp: null,
        };
        return this.companyService.updatePasswords(siret, body);
      })
    ).subscribe({
      next: () => {
        this.addingClient.set(false);
        const label = result.companyName || result.siren || 'Client';
        if (saveImpots) {
          this.notificationService.success(
            `${label} ajouté — identifiants Impots.gouv enregistrés. L’avis CFE sera disponible dès demain.`,
            4000
          );
        } else {
          this.notificationService.success(`${label} ajouté à vos clients`, 2500);
        }
        this.selectedAddSuggestion.set(null);
        this.addQuery.set('');
        this.addImpotsNom.set('');
        this.addImpotsPass.set('');
        this.addSuggestions.set([]);
        this.listPage.set(0);
        this.refreshClientsPaged();
      },
      error: () => {
        this.addingClient.set(false);
        this.notificationService.error('Erreur lors de l’ajout (client déjà présent ?)', 3000);
      },
    });
  }

  toggleInvitePanel(): void {
    this.showInvitePanel.update((current) => !current);
    if (!this.showInvitePanel()) {
      this.resetInviteForm();
    }
  }

  inviteClient(): void {
    const email = this.inviteEmail().trim().toLowerCase();
    if (!email || !email.includes('@')) {
      this.notificationService.error('Veuillez entrer un email valide', 3000);
      return;
    }

    const name = this.inviteName().trim();
    const siren = this.normalizeSirenOrSiret(this.inviteSiren()) ?? '';
    this.authService.inviteClient(email, name, siren).subscribe({
      next: () => {
        this.notificationService.success(`Invitation envoyée à ${email}`, 2500);
        this.resetInviteForm();
        this.showInvitePanel.set(false);
      },
      error: () => {
        this.notificationService.error(
          'Impossible d’envoyer l’invitation pour le moment. Veuillez réessayer.',
          3500
        );
      }
    });
  }

  openBulkImportModal(): void {
    const modalRef = this.modalService.open(ExpertBulkImportDialog, {
      title: 'Import de masse',
      data: {atCompanyLimit: this.atCompanyLimit()},
    });

    modalRef.afterClosed().subscribe((imported) => {
      if (imported) {
        this.listPage.set(0);
        this.refreshClientsPaged();
      }
    });
  }

  isExpanded(siret: string): boolean {
    return this.expandedRows().has(siret);
  }

  toggleRow(siret: string): void {
    const next = new Set(this.expandedRows());
    if (next.has(siret)) {
      next.delete(siret);
      this.expandedRows.set(next);
      return;
    }
    next.add(siret);
    this.expandedRows.set(next);
    if (!this.rowDetails()[siret]) {
      this.loadRowDetail(siret);
    }
  }

  impotsStatus(detail: IExpertClientDetail): ExpertStatusKind {
    return detail.impotsGouvLinked ? 'ok' : 'error';
  }

  cfeStatus(detail: IExpertClientDetail): ExpertStatusKind {
    if (detail.cfeRetrieved) {
      return 'ok';
    }
    return detail.impotsGouvLinked ? 'pending' : 'error';
  }

  fiscalStatus(detail: IExpertClientDetail): ExpertStatusKind {
    if (detail.fiscalActionsRetrieved) {
      return 'ok';
    }
    return detail.impotsGouvLinked ? 'pending' : 'error';
  }

  /** Statut global : vert = CFE récupéré, orange = en attente, rouge = identifiants manquants. */
  overallStatus(detail: IExpertClientDetail | undefined): ExpertStatusKind | null {
    if (!detail) {
      return null;
    }
    if (detail.cfeRetrieved) {
      return 'ok';
    }
    return detail.impotsGouvLinked ? 'pending' : 'error';
  }

  overallStatusTitle(kind: ExpertStatusKind | null): string {
    switch (kind) {
      case 'ok':
        return 'Avis CFE récupéré';
      case 'pending':
        return 'Identifiants enregistrés — avis CFE attendu dès demain';
      case 'error':
        return 'Identifiants Impots.gouv manquants';
      default:
        return 'Chargement du statut…';
    }
  }

  statusRowClass(kind: ExpertStatusKind): string {
    switch (kind) {
      case 'ok':
        return 'text-green-700';
      case 'pending':
        return 'text-orange-700';
      default:
        return 'text-red-700';
    }
  }

  statusIcon(kind: ExpertStatusKind): string {
    switch (kind) {
      case 'ok':
        return 'check_circle';
      case 'pending':
        return 'schedule';
      default:
        return 'cancel';
    }
  }

  statusIconClass(kind: ExpertStatusKind): string {
    switch (kind) {
      case 'ok':
        return 'text-green-600';
      case 'pending':
        return 'text-orange-600';
      default:
        return 'text-red-600';
    }
  }

  showsStoredPasswordPlaceholder(siret: string): boolean {
    const detail = this.rowDetails()[siret];
    if (!detail?.impotsPasswordStored) {
      return false;
    }
    return !this.rowPasswordPlainVisible()[siret] && !this.rowPasswordUserEdited()[siret];
  }

  passwordInputValue(siret: string): string {
    if (this.showsStoredPasswordPlaceholder(siret)) {
      return this.storedPasswordPlaceholder;
    }
    return this.rowPasswordDraft()[siret]?.pass ?? '';
  }

  onPasswordFocus(siret: string): void {
    if (this.showsStoredPasswordPlaceholder(siret)) {
      this.rowPasswordUserEdited.update((m) => ({...m, [siret]: true}));
      this.rowPasswordDraft.update((m) => ({
        ...m,
        [siret]: {nom: m[siret]?.nom ?? '', pass: ''},
      }));
    }
  }

  private loadRowDetail(siret: string): void {
    this.rowPasswordPlainVisible.update((m) => ({...m, [siret]: false}));
    this.rowPasswordUserEdited.update((m) => ({...m, [siret]: false}));
    this.rowDetailLoading.update((m) => ({...m, [siret]: true}));
    this.companyService.getExpertClientDetail(siret).subscribe({
      next: (detail) => {
        this.rowDetails.update((m) => ({...m, [siret]: detail}));
        this.rowPasswordDraft.update((m) => ({
          ...m,
          [siret]: {nom: detail.nomUtilisateurImpotsGouv ?? '', pass: ''},
        }));
        this.rowContactDraft.update((m) => ({
          ...m,
          [siret]: {name: detail.contactName ?? '', mail: detail.contactMail ?? ''},
        }));
        this.rowDetailLoading.update((m) => ({...m, [siret]: false}));
      },
      error: () => {
        this.rowDetailLoading.update((m) => ({...m, [siret]: false}));
        this.notificationService.error('Impossible de charger le détail du client', 3000);
      },
    });
  }

  updateContactDraft(siret: string, field: 'name' | 'mail', value: string): void {
    this.rowContactDraft.update((m) => {
      const cur = m[siret] ?? {name: '', mail: ''};
      return {...m, [siret]: {...cur, [field]: value}};
    });
  }

  saveRowContact(siret: string): void {
    const draft = this.rowContactDraft()[siret];
    if (!draft) {
      return;
    }
    const mail = draft.mail.trim();
    if (mail && !mail.includes('@')) {
      this.notificationService.error('Email \"' + mail + '\"  invalide', 3000);
      return;
    }
    this.rowContactSaving.update((m) => ({...m, [siret]: true}));
    const payload: ICompanyContactUpdate = {
      contactName: draft.name.trim() || null,
      contactMail: mail || null,
    };
    this.companyService.updateContact(siret, payload).subscribe({
      next: (updated) => {
        this.rowDetails.update((m) => {
          const current = m[siret];
          if (!current) {
            return m;
          }
          return {
            ...m,
            [siret]: {
              ...current,
              contactName: updated.contactName,
              contactMail: updated.contactMail,
            },
          };
        });
        this.rowContactDraft.update((m) => ({
          ...m,
          [siret]: {
            name: updated.contactName ?? '',
            mail: updated.contactMail ?? '',
          },
        }));
        this.rowContactSaving.update((m) => ({...m, [siret]: false}));
        this.notificationService.success('Contact enregistré', 2500);
      },
      error: () => {
        this.rowContactSaving.update((m) => ({...m, [siret]: false}));
        this.notificationService.error('Erreur lors de l’enregistrement du contact', 3000);
      },
    });
  }

  updateRowDraft(siret: string, field: 'nom' | 'pass', value: string): void {
    if (field === 'pass') {
      this.rowPasswordPlainVisible.update((m) => ({...m, [siret]: false}));
      if (value !== this.storedPasswordPlaceholder) {
        this.rowPasswordUserEdited.update((m) => ({...m, [siret]: true}));
      }
    }
    this.rowPasswordDraft.update((m) => {
      const cur = m[siret] ?? {nom: '', pass: ''};
      return {...m, [siret]: {...cur, [field]: value}};
    });
  }

  revealPassword(siret: string): void {
    this.rowRevealing.update((m) => ({...m, [siret]: true}));
    this.companyService.revealImpotsPassword(siret).subscribe({
      next: (res) => {
        this.rowPasswordUserEdited.update((m) => ({...m, [siret]: true}));
        this.rowPasswordDraft.update((m) => ({
          ...m,
          [siret]: {nom: m[siret]?.nom ?? '', pass: res.password},
        }));
        this.rowPasswordPlainVisible.update((m) => ({...m, [siret]: true}));
        this.rowRevealing.update((m) => ({...m, [siret]: false}));
      },
      error: () => {
        this.rowRevealing.update((m) => ({...m, [siret]: false}));
        this.notificationService.error('Impossible de révéler le mot de passe', 3000);
      },
    });
  }

  saveRowPasswords(siret: string): void {
    const draft = this.rowPasswordDraft()[siret];
    if (!draft) {
      return;
    }
    const keepExistingPassword = this.showsStoredPasswordPlaceholder(siret);
    const newPassword =
      !keepExistingPassword && draft.pass?.trim() && draft.pass !== this.storedPasswordPlaceholder
        ? draft.pass
        : null;
    this.rowSaving.update((m) => ({...m, [siret]: true}));
    const body: IPasswordUpdateRequest = {
      nomUtilisateurUrssaf: null,
      passwordUrssaf: null,
      typeCompteUrssaf: null,
      nomUtilisateurImpotsGouv: draft.nom || null,
      passwordImpotsGouv: newPassword,
      nomUtilisateurProvigis: null,
      passwordProvigis: null,
      nomUtilisateurIdenum: null,
      passwordIdenum: null,
      passwordProbtp: null,
      nomUtilisateurCibtp: null,
      passwordCibtp: null,
    };
    this.companyService.updatePasswords(siret, body).subscribe({
      next: () => {
        this.notificationService.success('Identifiants Impots.gouv enregistrés', 2500);
        this.rowSaving.update((m) => ({...m, [siret]: false}));
        this.loadRowDetail(siret);
        this.refreshClientsPaged();
      },
      error: () => {
        this.rowSaving.update((m) => ({...m, [siret]: false}));
        this.notificationService.error('Erreur lors de l’enregistrement', 3000);
      },
    });
  }

  downloadClientCfe(siret: string, companyName: string): void {
    const detail = this.rowDetails()[siret];
    if (!detail?.cfeDocumentId) {
      this.notificationService.error('Avis CFE non disponible pour ce client', 3000);
      return;
    }
    this.rowCfeDownloading.update((m) => ({...m, [siret]: true}));
    this.companyService.downloadDocument(detail.cfeDocumentId).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safeName = (companyName || siret).replace(/[^\w\s\-àâäéèêëïîôùûüÿçÀÂÄÉÈÊËÏÎÔÙÛÜŸÇ]/g, '_');
        const dateStr = new Date().toISOString().split('T')[0];
        a.download = `avis_cfe_${safeName}_${dateStr}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        this.notificationService.success('Avis CFE téléchargé', 2500);
        this.rowCfeDownloading.update((m) => ({...m, [siret]: false}));
      },
      error: () => {
        this.notificationService.error('Erreur lors du téléchargement de l’avis CFE', 3000);
        this.rowCfeDownloading.update((m) => ({...m, [siret]: false}));
      },
    });
  }

  removeClient(company: ICompanyDetailsSimplified): void {
    this.companyService.removeCompanyForUser(company.siret).subscribe({
      next: () => {
        this.notificationService.success(`${company.nomEntreprise} retiré de votre liste`, 2500);
        const next = new Set(this.expandedRows());
        next.delete(company.siret);
        this.expandedRows.set(next);
        this.refreshClientsPaged();
      },
      error: () => this.notificationService.error('Erreur lors de la suppression', 3000),
    });
  }

  downloadAllCfeZip(): void {
    if (this.totalClients() === 0) {
      this.notificationService.error('Aucun client à exporter', 2500);
      return;
    }
    this.downloadingCfeZip.set(true);
    this.companyService.downloadDocumentForAll('IMPOTS_CFE').subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cfe_clients_${new Date().toISOString().split('T')[0]}.zip`;
        a.click();
        URL.revokeObjectURL(url);
        this.notificationService.success('Archive CFE téléchargée', 2500);
        this.downloadingCfeZip.set(false);
      },
      error: () => {
        this.notificationService.error('Erreur lors du téléchargement de l’archive', 3500);
        this.downloadingCfeZip.set(false);
      },
    });
  }

  private resetInviteForm(): void {
    this.inviteEmail.set('');
    this.inviteName.set('');
    this.inviteSiren.set('');
  }

  private prefetchListStatuses(companies: ICompanyDetailsSimplified[]): void {
    if (!companies.length) {
      return;
    }
    forkJoin(
      companies.map((company) =>
        this.companyService.getExpertClientDetail(company.siret).pipe(
          catchError(() => of(null))
        )
      )
    ).subscribe((details) => {
      this.rowDetails.update((current) => {
        const next = {...current};
        companies.forEach((company, index) => {
          const detail = details[index];
          if (detail && !next[company.siret]) {
            next[company.siret] = detail;
            this.rowPasswordDraft.update((m) => ({
              ...m,
              [detail.siret]: {nom: detail.nomUtilisateurImpotsGouv ?? '', pass: ''},
            }));
            this.rowContactDraft.update((m) => ({
              ...m,
              [detail.siret]: {name: detail.contactName ?? '', mail: detail.contactMail ?? ''},
            }));
          }
        });
        return next;
      });
    });
  }

  private normalizeSirenOrSiret(raw: string): string | null {
    const cleaned = raw.replace(/[\s.\-]/g, '');
    if (/^\d{14}$/.test(cleaned)) {
      return cleaned;
    }
    if (/^\d{9}$/.test(cleaned)) {
      return cleaned;
    }
    return null;
  }

  handleBulkDownload(): void {
    const companies = this.companies();
    if (!companies.length) {
      this.notificationService.error('Ajoutez des clients d’abord', 2500);
      return;
    }
    this.downloading.set(true);

    forkJoin(companies.map((company) => this.companyService.getCertifications(company.siret))).subscribe({
      next: (certificationsByCompany) => {
        const rows: string[] = [];
        const headers = ['Client', 'SIREN', 'Certification', 'Type', 'Organisme', 'Début', 'Fin', 'Source'];
        companies.forEach((company, index) => {
          const certifications = certificationsByCompany[index] || [];
          certifications.forEach((cert: ICompanyCertification) => {
            rows.push(
              [
                company.nomEntreprise,
                company.siret.substring(0, 9),
                cert.label,
                cert.type,
                cert.organisme || '',
                cert.dateDebut || '',
                cert.dateFin || '',
                cert.source,
              ]
                .map((value) => `"${String(value).replace(/"/g, '""')}"`)
                .join(',')
            );
          });
        });

        if (!rows.length) {
          this.notificationService.error('Aucune certification trouvée pour vos clients', 3000);
          this.downloading.set(false);
          return;
        }

        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob(['\ufeff' + csv], {type: 'text/csv;charset=utf-8;'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `attestations_clients_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        this.notificationService.success(`${rows.length} certifications exportées`, 2500);
        this.downloading.set(false);
      },
      error: () => {
        this.notificationService.error('Erreur lors du téléchargement', 3000);
        this.downloading.set(false);
      },
    });
  }
}
